import { useState, useEffect } from 'preact/hooks';

interface ClaudeCodeUser {
  name: string;
  email: string;
  avatar?: string;
  organization?: string;
  organizationRole?: string;
  accountUuid?: string;
  organizationUuid?: string;
}

interface ClaudeCodeSubscription {
  type: string;
  status: string;
  hasAvailableSubscription: boolean;
  hasOpusPlanDefault: boolean;
  recommendedSubscription: string;
  isQualifiedForDataSharing: boolean;
}

interface ClaudeCodeFeatures {
  hasCompletedOnboarding: boolean;
  hasIdeOnboardingBeenShown: Record<string, boolean>;
  autoUpdatesProtectedForNative: boolean;
}

interface ClaudeCodeAuthState {
  isAuthenticated: boolean;
  user: ClaudeCodeUser | null;
  subscription: ClaudeCodeSubscription | null;
  features: ClaudeCodeFeatures | null;
  version: string | null;
}

export function useClaudeCodeAuth() {
  const [authState, setAuthState] = useState<ClaudeCodeAuthState>({
    isAuthenticated: false,
    user: null,
    subscription: null,
    features: null,
    version: null,
  });

  const checkClaudeCodeAuth = async () => {
    try {
      // Check if Byte DevOps is authenticated by running a simple command
      const response = await fetch('/api/claude/status', {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setAuthState({
            isAuthenticated: true,
            user: {
              name: data.user?.name || 'Claude User',
              email: data.user?.email || 'user@claude.ai',
              avatar: data.user?.avatar,
              organization: data.user?.organization,
              organizationRole: data.user?.organizationRole,
              accountUuid: data.user?.accountUuid,
              organizationUuid: data.user?.organizationUuid,
            },
            subscription: data.subscription || null,
            features: data.features || null,
            version: data.version || null,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            subscription: null,
            features: null,
            version: null,
          });
        }
      }
    } catch (error) {
      console.error('Failed to check Byte DevOps authentication:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        subscription: null,
        features: null,
        version: null,
      });
    }
  };

  useEffect(() => {
    // Check authentication on mount
    checkClaudeCodeAuth();
    
    // Check periodically for authentication changes
    const interval = setInterval(checkClaudeCodeAuth, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    setAuthState({
      isAuthenticated: false,
      user: null,
      subscription: null,
      features: null,
      version: null,
    });
  };

  return {
    ...authState,
    checkAuth: checkClaudeCodeAuth,
    logout,
  };
}
